const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export const normalizeGuid = (value) => {
    return value ? String(value).toLowerCase() : "";
};

export const getDepartmentId = (value) => {
    const departmentId =
        value?.departmentId ||
        value?.DepartmentId ||
        value?.serviceDepartmentId ||
        value?.ServiceDepartmentId ||
        value?.department?.id ||
        value?.Department?.id ||
        value?.department?.Id ||
        value?.Department?.Id ||
        value?.department?.departmentId ||
        value?.Department?.DepartmentId ||
        null;

    if (!departmentId || normalizeGuid(departmentId) === EMPTY_GUID) return null;
    return departmentId;
};

export const getKnownDepartmentName = (value) => {
    if (typeof value?.department === "string") return value.department;
    if (typeof value?.Department === "string") return value.Department;

    return (
        value?.departmentName ||
        value?.DepartmentName ||
        value?.serviceDepartmentName ||
        value?.ServiceDepartmentName ||
        value?.department?.name ||
        value?.Department?.name ||
        value?.department?.Name ||
        value?.Department?.Name ||
        value?.department?.departmentName ||
        value?.Department?.DepartmentName ||
        null
    );
};

export const getDepartmentName = (value) => {
    const departmentName = getKnownDepartmentName(value);

    return departmentName || "selected service department";
};

export const getTeamId = (team) => {
    return team?.id || team?.Id || team?.teamId || team?.TeamId || null;
};

export const getTeamName = (team) => {
    return getKnownTeamName(team) || "Assigned team";
};

export const getKnownTeamName = (team) =>
    team?.name || team?.Name || team?.teamName || team?.TeamName || null;

export const getIsTeamAssignmentValid = (service) =>
    service?.isTeamAssignmentValid ?? service?.IsTeamAssignmentValid;

export const getTeamAssignmentIssue = (service) =>
    service?.teamAssignmentIssue ?? service?.TeamAssignmentIssue ?? "";

export const getTeamAssignmentIssueMessage = (service) => {
    const issue = getTeamAssignmentIssue(service);
    const normalizedIssue = String(issue)
        .replace(/[\s_-]/g, "")
        .toLowerCase();

    const messages = {
        missingteam:
            "No team is assigned to this client for the service department.",
        missingteamlead: "No Team Leader is assigned to the matching team.",
        multipleteamleads:
            "Multiple Team Leaders are assigned to the matching team.",
        missingdepartment: "This service does not have a Department configured.",
        teamdepartmentmismatch:
            "The assigned Team does not match the Service Department.",
    };

    return messages[normalizedIssue] || issue;
};

const normalizeTeamAssignmentIssue = (issue) =>
    String(issue || "")
        .replace(/[^a-z0-9]/gi, "")
        .toLowerCase();

export const getTeamAssignmentIssueType = (service) => {
    const normalizedIssue = normalizeTeamAssignmentIssue(
        getTeamAssignmentIssue(service),
    );

    if (
        normalizedIssue === "missingteam" ||
        normalizedIssue.includes("noteamisassigned") ||
        normalizedIssue.includes("noteamisconfigured")
    ) {
        return "missingTeam";
    }

    if (
        normalizedIssue === "missingteamlead" ||
        normalizedIssue.includes("noteamleaderisassigned")
    ) {
        return "missingTeamLead";
    }

    if (
        normalizedIssue === "multipleteamleads" ||
        normalizedIssue.includes("multipleteamleaders")
    ) {
        return "multipleTeamLeads";
    }

    if (
        normalizedIssue === "missingdepartment" ||
        normalizedIssue.includes("doesnothaveadepartmentconfigured")
    ) {
        return "missingDepartment";
    }

    if (
        normalizedIssue === "teamdepartmentmismatch" ||
        normalizedIssue.includes("doesnotmatchtheservicedepartment")
    ) {
        return "teamDepartmentMismatch";
    }

    return "unknown";
};

export const getTeamConfigurationGuidance = (service) => {
    const issue = getTeamAssignmentIssueMessage(service);
    const issueType = getTeamAssignmentIssueType(service);
    const team = service?.team ?? service?.Team ?? null;
    const departmentSource = service?.service ?? service?.Service ?? service;
    const teamName = getKnownTeamName(team);
    const departmentName =
        getKnownDepartmentName(departmentSource) || getKnownDepartmentName(team);

    if (issueType === "missingTeam") {
        return {
            issue: departmentName
                ? `No team is assigned to the ${departmentName} department.`
                : "No team is assigned to this service's department.",
            action: "Update the client's teams configuration before continuing by clicking the Edit Client Teams button.",
            teamName,
            departmentName,
        };
    }

    if (issueType === "missingTeamLead") {
        return {
            issue: teamName
                ? `${teamName} does not have a team leader.`
                : "The matching team does not have a team leader.",
            action: "Assign a team leader in the client's teams configuration before continuing.",
            teamName,
            departmentName,
        };
    }

    if (issueType === "multipleTeamLeads") {
        return {
            issue: teamName
                ? `${teamName} has multiple Team Leaders.`
                : "The matching team has multiple Team Leaders.",
            action: "Update the team so exactly one Team Leader is assigned before continuing.",
            teamName,
            departmentName,
        };
    }

    if (issueType === "missingDepartment") {
        return {
            issue: "This service does not have a Department configured.",
            action: "Go to the Service configuration, assign a Department to this service, and save your changes before continuing.",
            teamName,
            departmentName,
        };
    }

    if (issueType === "teamDepartmentMismatch") {
        return {
            issue:
                teamName && departmentName
                    ? `${teamName} is not configured for the ${departmentName} department.`
                    : "The assigned team does not match this service's department.",
            action: "Go to the client's Teams section and assign the team that corresponds to this service's department before continuing.",
            teamName,
            departmentName,
        };
    }

    return {
        issue: issue || "This service does not have a valid team configuration.",
        action: teamName
            ? "Go to the client's Teams section → Edit Team → correct the indicated configuration → Save."
            : "Go to the client's Teams section and correct the indicated team configuration before continuing.",
        teamName,
        departmentName,
    };
};

export const getClientServiceSubscriptionId = (service) =>
    service?.clientServiceSubscriptionId ??
    service?.ClientServiceSubscriptionId ??
    service?.id ??
    service?.Id ??
    null;

export const getClientServiceName = (service) =>
    service?.serviceName ??
    service?.ServiceName ??
    service?.name ??
    service?.Name ??
    service?.service?.name ??
    service?.Service?.Name ??
    "This service";

export const getInvalidTeamAssignmentServiceIds = (services = []) => [
    ...new Set(
        services
            .filter((service) => getIsTeamAssignmentValid(service) === false)
            .map(getClientServiceSubscriptionId)
            .filter(Boolean),
    ),
];

export const findClientTeamForServiceDepartment = (clientTeams, departmentId) => {
    const normalizedDepartmentId = normalizeGuid(departmentId);

    if (!normalizedDepartmentId) return null;

    return (
        (clientTeams || []).find((team) => {
            return normalizeGuid(getDepartmentId(team)) === normalizedDepartmentId;
        }) || null
    );
};
